import React, { useEffect, useState } from 'react';
import {
  Alert, Badge, Container, ListGroup, ProgressBar, Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getMyReviewSets } from '@/api/reviewSetApi';
import type { ReviewSetInstanceSummaryDto } from '@/types/reviews';
import './MyReviews.scss';

const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : null);

const statusVariant = (s: ReviewSetInstanceSummaryDto) => {
  if (s.isComplete) return 'success';
  if (s.progressPercent > 0) return 'primary';
  return 'secondary';
};

const MyReviewSets: React.FC = () => {
  const [sets, setSets] = useState<ReviewSetInstanceSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handleFocus = () => load();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getMyReviewSets();
      setSets(data);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load review sets.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading review sets…</p>
      </Container>
    );
  }

  const open = sets.filter((s) => !s.isComplete);
  const done = sets.filter((s) => s.isComplete);

  const renderSet = (s: ReviewSetInstanceSummaryDto) => {
    const due = formatDate(s.dueDate);
    const primaryOwner = s.owners.find((o) => o.isPrimary);
    const otherOwners = s.owners.filter((o) => !o.isPrimary);

    return (
      <ListGroup.Item
        key={s.setInstanceGuid}
        as={Link}
        to={`/review-sets/${s.setInstanceGuid}`}
        action
        className="d-flex flex-column gap-1 py-3"
      >
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <strong>{s.setInstanceName}</strong>
            <Badge bg="secondary" className="ms-2">{s.setTemplateCategory}</Badge>
          </div>
          {s.isComplete
            ? <Badge bg="success">Complete</Badge>
            : <Badge bg={s.progressPercent > 0 ? 'primary' : 'warning'} text={s.progressPercent > 0 ? undefined : 'dark'}>
                {s.progressPercent > 0 ? 'In Progress' : 'Not Started'}
              </Badge>
          }
        </div>

        <div className="text-secondary small">
          {s.setTemplateName}
          {due && ` · Due ${due}`}
          {s.completedDate && ` · Completed ${formatDate(s.completedDate)}`}
        </div>

        {s.owners.length > 0 && (
          <div className="text-secondary small">
            {primaryOwner && <span>Primary: <strong>{primaryOwner.ownerEmail}</strong></span>}
            {otherOwners.length > 0 && (
              <span className="ms-2">+{otherOwners.length} other owner{otherOwners.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        )}

        <ProgressBar
          now={s.progressPercent}
          label={`${s.completedReviews} / ${s.totalReviews} reviews`}
          variant={statusVariant(s)}
          style={{ height: 16, marginTop: 4 }}
        />
      </ListGroup.Item>
    );
  };

  return (
    <Container className="mt-4 my-reviews-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">My Review Sets</h4>
        <Link to="/reviews" className="small text-secondary">Individual Reviews →</Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {sets.length === 0 && (
        <Alert variant="secondary">You have no review sets assigned.</Alert>
      )}

      {open.length > 0 && (
        <>
          <h6 className="text-secondary text-uppercase small mb-2">Open</h6>
          <ListGroup className="mb-4">{open.map(renderSet)}</ListGroup>
        </>
      )}

      {done.length > 0 && (
        <>
          <h6 className="text-secondary text-uppercase small mb-2">Completed</h6>
          <ListGroup>{done.map(renderSet)}</ListGroup>
        </>
      )}
    </Container>
  );
};

export default MyReviewSets;
